import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BuildingDirectoryService from '../../services/BuildingDirectoryService';
import { toast } from 'react-toastify';
import BuildingDirectoryInfo from './models/buildingDirectoryInfo';
import lookupIcon from '../../assets/images/lookup-icon.png';
import buildingIcon from '../../assets/images/building-icon.png';
import { useHistory, useParams } from 'react-router-dom';
import RouteParams from '../../interfaces/general/Route';
import SearchMenuPanel from './components/SearchMenuPanel';
import FilterMenuPanel from './components/FilterMenuPanel';
import IPropertyInfo from '../../components/shared/PropertiesMap/iPropertyInfo';
import PropertiesMap from '../../components/shared/PropertiesMap/PropertiesMap';
import MainLoader from '../../components/shared/MainLoader';

const MapFullHeight = 'calc(100vh)';

const SearchingMap: FunctionComponent = ({ children }) => {
    const { t } = useTranslation(['f', 'b']),
        history = useHistory(),
        { id } = useParams<RouteParams>(),
        [selectedMenu, setSelectedMenu] = useState<number>(0),
        [isLoading, setIsLoading] = useState<boolean>(false),
        [mapHeight, setMapHeight] = useState<string>(MapFullHeight),
        [allBuildingDirectories, setAllBuildingDirectories] = useState<Array<BuildingDirectoryInfo>>([]),
        [buildingDirectories, setBuildingDirectories] = useState<Array<BuildingDirectoryInfo>>([]),
        [selectedTenants, setSelectedTenants] = useState<string[]>([]),
        [selectedSectors, setSelectedSectors] = useState<string[]>([]),
        [selectedPriceRange, setSelectedPriceRange] = useState<[number, number]>([0, 50]),
        [minPrice, setMinPrice] = useState<number>(0),
        [maxPrice, setMaxPrice] = useState<number>(50);
    const filterApplyTimeoutRef = useRef<number | null>(null);

    const fetchAllBuildingDirectories = useCallback(async () => {
        try {
            setIsLoading(true);
            const buildingDirectories = await BuildingDirectoryService.fetchBuildingDirectories();

            const values = buildingDirectories
                .map(item => {
                    const dynamicItem = item as any;
                    const rawValue = dynamicItem.price ?? dynamicItem.p ?? dynamicItem.nla ?? dynamicItem.la ?? dynamicItem.landArea;
                    const parsed = Number(rawValue);
                    return isNaN(parsed) ? undefined : parsed;
                })
                .filter((value): value is number => value !== undefined);
            const nextMinPrice = values.length > 0 ? Math.floor(Math.min(...values)) : 0;
            const nextMaxPrice = values.length > 0 ? Math.ceil(Math.max(...values)) : 50;

            setMinPrice(nextMinPrice);
            setMaxPrice(nextMaxPrice);
            setSelectedPriceRange([nextMinPrice, nextMaxPrice]);
            setAllBuildingDirectories(buildingDirectories);
            setBuildingDirectories(buildingDirectories);
        } catch (error: any) {
            toast(t(`b:${error.response.data.errorMessage}`));
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAllBuildingDirectories();
    }, []);

    useEffect(() => {
        setMapHeight(id ? 'calc(50vh - 30px)' : MapFullHeight);
    }, [id]);

    const handleBuildingClicked = (property: IPropertyInfo) => {
        history.push(`/building-directory/${property.id}`);
    };

    useEffect(() => {
        if (allBuildingDirectories.length === 0) {
            return;
        }

        if (filterApplyTimeoutRef.current) {
            window.clearTimeout(filterApplyTimeoutRef.current);
        }

        setIsLoading(true);
        filterApplyTimeoutRef.current = window.setTimeout(() => {
            let filtered = [...allBuildingDirectories];

            if (selectedTenants.length > 0) {
                const tenantSet = new Set(selectedTenants.map(v => v.toLowerCase()));
                filtered = filtered.filter(item => {
                    const secondarySector = ((item as any).secondarySector || '').toString().toLowerCase();
                    return secondarySector ? tenantSet.has(secondarySector) : false;
                });
            }

            if (selectedSectors.length > 0) {
                const sectorSet = new Set(selectedSectors.map(v => v.toLowerCase()));
                filtered = filtered.filter(item => {
                    const primarySector = ((item as any).primarySector || '').toString().toLowerCase();
                    return primarySector ? sectorSet.has(primarySector) : false;
                });
            }

            const [min, max] = selectedPriceRange;
            filtered = filtered.filter(item => {
                const dynamicItem = item as any;
                const rawValue = dynamicItem.price ?? dynamicItem.p ?? dynamicItem.nla ?? dynamicItem.la ?? dynamicItem.landArea;
                const parsed = Number(rawValue);
                if (isNaN(parsed)) {
                    return true;
                }
                return parsed >= min && parsed <= max;
            });

            setBuildingDirectories(filtered);
            setIsLoading(false);
        }, 180);
    }, [allBuildingDirectories, selectedTenants, selectedSectors, selectedPriceRange]);

    useEffect(() => {
        return () => {
            if (filterApplyTimeoutRef.current) {
                window.clearTimeout(filterApplyTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="property-hero searching-map">
            <figure>
                <div className="background-cover">
                    <div className='navigation-icon'>
                        <img src={buildingIcon} onClick={() => setSelectedMenu(0)} />
                        <hr />
                        <img src={lookupIcon} onClick={() => setSelectedMenu(1)} />
                    </div>
                    <div className='search-content'>
                        <div className='search-panel'>
                            <div className='search-menu' style={{ display: selectedMenu === 0 ? 'block' : 'none' }}>
                                <FilterMenuPanel
                                    minPrice={minPrice}
                                    maxPrice={maxPrice}
                                    selectedTenants={selectedTenants}
                                    selectedSectors={selectedSectors}
                                    selectedPriceRange={selectedPriceRange}
                                    onTenantsChanged={setSelectedTenants}
                                    onSectorsChanged={setSelectedSectors}
                                    onPriceChanged={setSelectedPriceRange}
                                    results={buildingDirectories}
                                    onBuildingClicked={handleBuildingClicked}
                                />
                            </div>
                            <div className='search-menu' style={{ display: selectedMenu === 1 ? 'block' : 'none' }}>
                                <SearchMenuPanel onBuildingClicked={handleBuildingClicked} />
                            </div>
                        </div>
                        <div className="map-panel">
                            <PropertiesMap type={BuildingDirectoryInfo} height={mapHeight} markerId={id} properties={buildingDirectories} onMarkerClicked={handleBuildingClicked} />
                            {!isLoading ? null : <div className='loading'>
                                <MainLoader type="regular" size={100} />
                            </div>}
                            {children}
                        </div>
                    </div>
                </div>
            </figure>
        </div>
    );
};

export default SearchingMap;
